/* eslint-disable camelcase */
import { IMapyBoxActivityFilter, IMapyBoxPlacesFilter, TActivityType, TMapyBoxType, TVisited } from "~/interfaces";
import { node } from "~/util/dom";
import MyMapsFilterRouteTypes, { IMyMapsFilterRouteTypesFilterData } from "./filter-route-types";
import MyMapsFilterItem from "./filter-item";
import { _ } from "~/i18n";
import { openPropositionDialog } from "~/util/premium";
import MyMapsFilterSeasonChart, { IMyMapsSeasonChartsFilterData } from "./filter-season-chart";
import { criterion2activityType } from "~/route/utils";
import ContextMenu from "~/ui/contextmenu";
import ContextMenuItem from "~/ui/contextmenuitem";

interface IFilterData {
	years: {
		[key: string]: {
			[key: string]: number;
		};
	};
	// aktivity
	activities?: Array<TActivityType>;
	// slozky
	track_type_list?: Array<TActivityType>;
	entity_types_list?: Array<TMapyBoxType>;
	criterion_list?: Array<number>;
	visitedCount: number;
	visited_count?: number;
}

const DEF_COUNT = -1;

interface IOpts {
	isPremiumUser: boolean;
	premiumType: "premium-all" | "premium-visited";
	filterData: IFilterData
	onChange?: () => void;
	onReset?: () => void;
	onAnalytics?: (type: "entity_type" | "visited" | "period") => void;
}

function isDataContains(data: IFilterData, type: "places" | "measure" | "visited") {
	const entitySources = data.entity_types_list || [];
	let allowedTypes: Array<TMapyBoxType> = [];

	switch (type) {
		case "places":
			allowedTypes = ["set", "point", "entity"];
			break;

		case "measure": {
			allowedTypes = ["measure"];
			break;
		}

		case "visited": {
			const visitedCount = data.visitedCount || data.visited_count || 0;

			return visitedCount > 0;
		}

		default:
	}

	if (entitySources.length === 0) {
		return false;
	}

	for (const entity of allowedTypes) {
		if (entitySources.includes(entity)) {
			return true;
		}
	}

	return false;
}

function getTrackSources(data: IFilterData) {
	const source = data.track_type_list || data.activities || [];
	const copied = source.slice();

	if (data.criterion_list) {
		for (const criterion of data.criterion_list) {
			const type = criterion2activityType(criterion);

			if (type && !copied.includes(type)) {
				copied.push(type);
			}
		}
	}

	return copied;
}

function openProposition() {
	Mapy.getComponent("logger").log("MyMapsFilters_premiumPurchase_show");
	openPropositionDialog("MyMapsFilters");
}

const HIDE_CLASS = "hide";

export default class MyMapsFilter {
	_opts: IOpts;

	_dom: {
		container: HTMLDivElement;
		reset: HTMLDivElement;
	};

	_filter: {
		routeTypes: MyMapsFilterRouteTypes;
		seasonChart: MyMapsFilterSeasonChart;
	};

	_filterItems: {
		routeTypes: MyMapsFilterItem;
		visited: MyMapsFilterItem;
		seasonChart: MyMapsFilterItem;
	};

	_filterData: {
		lastRouteTypesCount: number;
		routeTypes: IMyMapsFilterRouteTypesFilterData;
		visited: TVisited;
		seasonChart: IMyMapsSeasonChartsFilterData;
	};

	_filterDataOrig: {
		lastRouteTypesCount: number;
		routeTypes: IMyMapsFilterRouteTypesFilterData;
		visited: TVisited;
		seasonChart: IMyMapsSeasonChartsFilterData;
	};

	_isRouteTypeOpen: boolean;

	_isSeasonChartOpen: boolean;

	_changed: boolean;

	constructor(opts: IOpts) {
		this._opts = {
			isPremiumUser: false,
			premiumType: "premium-all",
			filterData: null,
			onChange: () => {},
			onReset: () => {},
			onAnalytics: () => {},
			...opts,
		};
		this._dom = {
			container: node("div", { className: "mymaps__filter" }),
			reset: node("div", { className: "mymaps__filterReset" }),
		};
		this._isRouteTypeOpen = false;
		this._isSeasonChartOpen = false;
		this._changed = false;
		this._updateResetBtn();

		this._build();
		document.addEventListener("pointerup", this);
	}

	destroy() {
		this._filter.seasonChart.destroy();
		document.removeEventListener("pointerup", this);
		this._dom.container.remove();
	}

	get container() {
		return this._dom.container;
	}

	get resetCont() {
		return this._dom.reset;
	}

	get changed() {
		return this._changed;
	}

	get placesFilter(): IMapyBoxPlacesFilter | null {
		if (!this._changed) {
			return null;
		}

		return {
			...this._filterData.visited === "all" ? {} : { visited: this._filterData.visited === "visited" },
			types: {
				...this._filterData.routeTypes.hasPlaces
					? {
						point_entities: this._filterData.routeTypes.places,
					}
					: {},
				...this._filterData.routeTypes.hasDistanceMeter
					? {
						measure_entities: this._filterData.routeTypes.distancemeter,
					}
					: {},
				route_entities: {
					track_type_list: this._filterData.routeTypes.selTypes,
					route_criterion_list: this._filterData.routeTypes.selCriterions,
				},
			},
			...this._filterData.seasonChart.isAll
				? {}
				: {
					date_range: {
						date_from: this._filterData.seasonChart.fromDate,
						date_to: this._filterData.seasonChart.toDate,
					},
				},
		};
	}

	get activitiesFilter(): IMapyBoxActivityFilter | null {
		if (!this._changed) {
			return null;
		}

		return {
			...this._filterData.visited === "all" ? {} : { visited: this._filterData.visited === "visited" },
			track_type: this._filterData.routeTypes.selTypes,
			...this._filterData.seasonChart.isAll
				? {}
				: {
					date_from: this._filterData.seasonChart.fromDate,
					date_to: this._filterData.seasonChart.toDate,
				},
		};
	}

	reset() {
		this._filter.routeTypes.reset();
		this._filter.seasonChart.reset();
		this._filterItems.visited.reset();
		this._filterData = structuredClone(this._filterDataOrig);
		this._updateRouteTypeItem(DEF_COUNT);
		this._updateSeaonChartItem();
		this._changed = false;
		this._updateResetBtn();
	}

	handleEvent(event) {
		if (event.type === "pointerup") {
			if (this._isRouteTypeOpen) {
				// klik mimo, nebo na tlacitko
				if (!this._filter.routeTypes.container.contains(event.target) && !this._filterItems.routeTypes.container.contains(event.target)) {
					this._updateRouteTypeFilter(this._filter.routeTypes.filter, this._filter.routeTypes.count);
				}
			} else if (this._isSeasonChartOpen) {
				// klik mimo, nebo na tlacitko
				if (!this._filter.seasonChart.container.contains(event.target) && !this._filterItems.seasonChart.container.contains(event.target)) {
					this._updateSeasonChartFilter(this._filter.seasonChart.filter);
				}
			}
		}
	}

	_build() {
		// route types
		const trackSources = getTrackSources(this._opts.filterData);
		const criterionList = this._opts.filterData.criterion_list || [];
		const hasPlaces = isDataContains(this._opts.filterData, "places");
		const hasDistanceMeter = isDataContains(this._opts.filterData, "measure");
		const routeTypes = new MyMapsFilterRouteTypes({
			hasPlaces,
			hasDistanceMeter,
			isPremiumUser: this._opts.isPremiumUser || this._opts.premiumType === "premium-visited",
			filter: {
				places: true,
				distancemeter: true,
				allRoutes: true,
				selTypes: trackSources,
				allTypes: trackSources.slice(),
				selCriterions: criterionList,
				allCriterions: criterionList.slice(),
				hasPlaces,
				hasDistanceMeter,
			},
			onChange: (data, count) => {
				this._updateRouteTypeFilter(data, count);

				if (count > DEF_COUNT) {
					this._opts.onAnalytics("entity_type");
				}
			},
			onNonPremiumClick: () => {
				this._filterWindow("route-types", "close");
				// otevreme propozici
				openProposition();
			},
		});
		const routeTypesItem = new MyMapsFilterItem({
			config: {
				title: _("mymap.filters.type"),
				icon: "dropdown",
			},
			onClick: () => {
				this._filterWindow("route-types", "toggle");
			},
		});

		// visited
		const hasVisited = isDataContains(this._opts.filterData, "visited");
		const visitedIsDisabled = !this._opts.isPremiumUser || !hasVisited;
		let visitedMenu: ContextMenu = null;
		const visitedItem = new MyMapsFilterItem({
			config: {
				title: _("mymap.filters.visited.defaultTitle"),
				icon: this._opts.isPremiumUser
					? "dropdown"
					: "premium",
				disabled: visitedIsDisabled,
				disabledTooltip: hasVisited
					? ""
					: _("mymap.filters.noVisitedOpt", {
						br: "<br /><br />",
						strongStart: "<strong>",
						strongEnd: "</strong>",
					}),
			},
			onClick: () => {
				if (visitedIsDisabled) {
					if (!this._opts.isPremiumUser) {
						// otevreme propozici
						openProposition();
					}
				} else {
					if (!visitedMenu) {
						visitedMenu = new ContextMenu({
							anchor: visitedItem.container,
							position: "bottom-start",
							offset: 8,
							className: "mymaps__filterVisitedMenu",
						});

						([
							{ title: "mymap.filters.visited.all", value: "all" },
							{ title: "mymap.filters.visited.visited", value: "visited" },
							{ title: "mymap.filters.visited.unvisited", value: "unvisited" },
						] as Array<{ title: string; value: TVisited }>).forEach(item => {
							const menuItem = new ContextMenuItem({
								text: _(item.title),
								onClick: event => {
									event.stopPropagation();
									visitedMenu.close();
									this._filterData.visited = item.value;
									visitedItem.update = {
										active: this._filterData.visited !== "all",
										title: this._filterData.visited === "all" ? _("mymap.filters.visited.defaultTitle") : _(item.title),
									};
									this._done();
									if (this._filterData.visited !== "all") {
										this._opts.onAnalytics("visited");
									}
								},
							});

							menuItem.container.classList.add("mymaps__filterVisitedMenuItem");
							visitedMenu.container.appendChild(menuItem.container);
						});
					}

					visitedMenu.toggle();
				}
			},
		});

		// obdobi
		const seasonChart = new MyMapsFilterSeasonChart({
			isPremiumUser: this._opts.isPremiumUser || this._opts.premiumType === "premium-visited",
			data: this._opts.filterData.years || {},
			onChange: filterData => {
				this._updateSeasonChartFilter(filterData);

				if (!filterData.isAll) {
					this._opts.onAnalytics("period");
				}
			},
			onNonPremiumClick: () => {
				this._filterWindow("season-chart", "close");
				// otevreme propozici
				openProposition();
			},
		});
		const seasonChartItem = new MyMapsFilterItem({
			config: {
				title: _("mymap.activity.filter.wholeSeason"),
				icon: "dropdown",
			},
			onClick: () => {
				this._filterWindow("season-chart", "toggle");
			},
		});

		// ulozime
		this._filter = {
			routeTypes,
			seasonChart,
		};
		this._filterItems = {
			routeTypes: routeTypesItem,
			visited: visitedItem,
			seasonChart: seasonChartItem,
		};
		this._filterData = {
			lastRouteTypesCount: DEF_COUNT,
			routeTypes: routeTypes.filter,
			visited: "all",
			seasonChart: seasonChart.filter,
		};
		this._filterDataOrig = structuredClone(this._filterData);

		// dom
		seasonChartItem.container.classList.add("season-chart-item");
		this._dom.container.append(routeTypesItem.container, visitedItem.container, seasonChartItem.container);

		// reset tlacitko
		const resetBtn = node("button", { className: "mymaps__filterResetBtn", textContent: _("mymap.filters.resetFilter"), type: "button" });

		resetBtn.addEventListener("click", () => {
			this.reset();
			this._opts.onReset();
		});
		this._dom.reset.append(resetBtn);
	}

	_filterWindow(type: "route-types" | "season-chart", mode: "close" | "toggle") {
		switch (type) {
			case "route-types": {
				let newState = this._isRouteTypeOpen;

				if (mode === "toggle") {
					newState = !newState;
				} else {
					newState = false;
				}

				if (newState) {
					this._dom.container.append(this._filter.routeTypes.container);
				} else {
					this._filter.routeTypes.container.remove();
				}

				this._isRouteTypeOpen = newState;
				break;
			}

			case "season-chart": {
				let newState = this._isSeasonChartOpen;

				if (mode === "toggle") {
					newState = !newState;
				} else {
					newState = false;
				}

				if (newState) {
					this._dom.container.append(this._filter.seasonChart.container);
				} else {
					this._filter.seasonChart.container.remove();
				}

				this._isSeasonChartOpen = newState;
				break;
			}

			default:
		}
	}

	_updateRouteTypeItem(count: number) {
		this._filterItems.routeTypes.update = {
			active: count > DEF_COUNT,
			title: count === DEF_COUNT
				? _("mymap.filters.type")
				: `${_("mymap.filters.type")} (${count})`,
		};
	}

	_updateSeaonChartItem() {
		this._filterItems.seasonChart.update = {
			active: !this._filterData.seasonChart.isAll,
			title: this._filterData.seasonChart.title,
		};
	}

	_updateRouteTypeFilter(data: IMyMapsFilterRouteTypesFilterData, count: number) {
		const filterChange = this._filterData.lastRouteTypesCount !== count;

		this._filterData.routeTypes = data;
		this._filterData.lastRouteTypesCount = count;
		this._filterWindow("route-types", "close");
		this._updateRouteTypeItem(count);

		if (filterChange) {
			this._done();
		}
	}

	_updateSeasonChartFilter(data: IMyMapsSeasonChartsFilterData) {
		const filterChange = (this._filterData.seasonChart.isAll && !data.isAll) || (!this._filterData.seasonChart.isAll && data.isAll) || (this._filterData.seasonChart.title !== data.title);

		this._filterData.seasonChart = data;
		this._filterWindow("season-chart", "close");
		this._updateSeaonChartItem();

		if (filterChange) {
			this._done();
		}
	}

	_updateResetBtn() {
		this._dom.reset.classList[this._changed ? "remove" : "add"](HIDE_CLASS);
	}

	_done() {
		// signal
		this._changed = this._filter.routeTypes.count !== DEF_COUNT || this._filterData.visited !== "all" || !this._filter.seasonChart.filter.isAll;

		this._updateResetBtn();
		this._opts.onChange();
	}
}
